<script setup>
/* eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text */
/**
 * Página admin: cadastrar URLs do app tenant e copiar URL de webhook do Chatwoot (hub 195).
 * Efeito em produção só após migrate + mounts + restart autorizado.
 */
import { onMounted, ref } from 'vue';
import { useAlert } from 'dashboard/composables';
import TenantMessageSourcesAPI from 'dashboard/api/tenantMessageSources';

const sources = ref([]);
const loading = ref(false);
const form = ref({
  slug: '',
  mensagens_url: '',
  conversas_url: '',
  api_token: '',
  sync_mode: 'both',
  enabled: false,
});

const load = async () => {
  loading.value = true;
  try {
    const { data } = await TenantMessageSourcesAPI.get();
    sources.value = data.payload || [];
  } catch (e) {
    useAlert('Falha ao carregar fontes do bridge');
  } finally {
    loading.value = false;
  }
};

const createSource = async () => {
  try {
    await TenantMessageSourcesAPI.create(form.value);
    useAlert('Fonte criada (enabled=false por padrão)');
    form.value = {
      slug: '',
      mensagens_url: '',
      conversas_url: '',
      api_token: '',
      sync_mode: 'both',
      enabled: false,
    };
    await load();
  } catch (e) {
    useAlert('Erro ao criar fonte');
  }
};

const toggleEnabled = async source => {
  try {
    await TenantMessageSourcesAPI.update(source.id, {
      enabled: !source.enabled,
    });
    await load();
  } catch (e) {
    useAlert('Erro ao atualizar');
  }
};

const copyWebhook = async source => {
  try {
    await navigator.clipboard.writeText(source.webhook_url);
    useAlert('URL de webhook copiada');
  } catch (e) {
    useAlert(source.webhook_url);
  }
};

onMounted(load);
</script>

<template>
  <div class="flex flex-col gap-6 p-6 max-w-4xl">
    <div>
      <h1 class="text-xl font-semibold text-n-slate-12">
        Bridge de histórico (apps IA)
      </h1>
      <p class="text-sm text-n-slate-11 mt-1">
        Pull das APIs do tenant e URL para o hub (Meta) enviar push. Não altera
        Evolution 2.25 (UAZAPI off até segunda ordem). Fontes nascem desligadas.
      </p>
    </div>

    <section class="flex flex-col gap-3 border border-n-weak rounded-lg p-4">
      <h2 class="font-medium text-n-slate-12">Nova fonte</h2>
      <input
        v-model="form.slug"
        class="input"
        placeholder="slug (ex.: alumax)"
      />
      <input
        v-model="form.mensagens_url"
        class="input"
        placeholder="URL GET /api/mensagens"
      />
      <input
        v-model="form.conversas_url"
        class="input"
        placeholder="URL GET /api/mensagens/conversas"
      />
      <input
        v-model="form.api_token"
        class="input"
        type="password"
        placeholder="API_MENSAGENS_TOKEN"
      />
      <select v-model="form.sync_mode" class="input">
        <option value="pull">pull</option>
        <option value="push">push</option>
        <option value="both">both</option>
      </select>
      <button type="button" class="button nice primary" @click="createSource">
        Criar
      </button>
    </section>

    <section v-if="loading" class="text-n-slate-11">Carregando…</section>
    <section v-else class="flex flex-col gap-4">
      <div
        v-for="s in sources"
        :key="s.id"
        class="border border-n-weak rounded-lg p-4 flex flex-col gap-2"
      >
        <div class="flex items-center justify-between gap-2">
          <strong class="text-n-slate-12">{{ s.slug }}</strong>
          <span class="text-xs text-n-slate-11">
            {{ s.sync_mode }} · {{ s.enabled ? 'ligado' : 'desligado' }}
          </span>
        </div>
        <div class="text-xs text-n-slate-11 break-all">
          Pull: {{ s.mensagens_url }}
        </div>
        <div class="text-xs text-n-slate-11">
          Token app: {{ s.api_token_masked }}
        </div>
        <div class="text-xs text-n-slate-12 break-all font-mono">
          Webhook hub: {{ s.webhook_url }}
        </div>
        <div class="flex gap-2 mt-1">
          <button
            type="button"
            class="button nice small"
            @click="copyWebhook(s)"
          >
            Copiar URL webhook
          </button>
          <button
            type="button"
            class="button nice small secondary"
            @click="toggleEnabled(s)"
          >
            {{ s.enabled ? 'Desligar' : 'Ligar' }}
          </button>
        </div>
        <p v-if="s.last_error" class="text-xs text-red-500">
          {{ s.last_error }}
        </p>
      </div>
      <p v-if="!sources.length" class="text-sm text-n-slate-11">
        Nenhuma fonte nesta account. Use o seed (rake) ou o formulário acima.
      </p>
    </section>
  </div>
</template>
